import { config } from '@cfg';
import ClickAwayListener from '@components/ClickAwayListener/ClickAwayListener';
import { propertyTypesToBuy, propertyTypesToRent } from '@components/PropertiesSearch/utils';
import { Prediction, PredictionsResponse, PredictionTypeEnum } from '@engelvoelkersamericas/api';
import { Magnifier } from '@icon/Magnifier';
import { CircularProgress } from '@mui/material';
import { useAction } from '@util/customHooks/useAction';
import useLocationToGeocode from '@util/customHooks/useLocationToGeoCode';
import { apiClient } from 'api-client';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { usePostHog } from 'posthog-js/react';
import CityIcon from 'public/assets/icons/city.svg';
import GlobeIcon from 'public/assets/icons/globe.svg';
import NeighborhoodIcon from 'public/assets/icons/neighborhood.svg';
import PinIcon from 'public/assets/icons/pin.svg';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  AutocompleteDropdown,
  PredictionGroup,
  PredictionGroupHeader,
  PredictionItem,
  PredictionItemText,
  PredictionType,
  SearchInput,
  SearchInputIcon,
  SearchInputWrapper,
  SearchWrapper,
  ViewMoreButton,
} from './ElasticAutocomplete.styles';
import SearchTabs from './SearchTabs';

const USA = 'us';
const CANADA = 'ca';
const MEXICO = 'mx';
const BAHAMAS = 'bs';
const BELIZE = 'bz';
const COSTA_RICA = 'cr';
const CAYMAN_ISLANDS = 'ky';
const ST_MAARTEN = 'sx';
const TURKS_AND_CAICOS_ISLANDS = 'tc';
const PANAMA = 'pa';

// ! IMPORTANT: the order of the countries determines the order
// ! of search results, so be careful about adding and moving countries
const DEFAULT_COUNTRIES = [
  PANAMA,
  USA,
  CANADA,
  BELIZE,
  MEXICO,
  BAHAMAS,
  COSTA_RICA,
  CAYMAN_ISLANDS,
  ST_MAARTEN,
  TURKS_AND_CAICOS_ISLANDS,
];

const predictionTypeIcons: Record<PredictionTypeEnum, string> = {
  [PredictionTypeEnum.Country]: GlobeIcon,
  [PredictionTypeEnum.State]: PinIcon,
  [PredictionTypeEnum.City]: CityIcon,
  [PredictionTypeEnum.CityRegion]: PinIcon,
  [PredictionTypeEnum.Neighborhood]: NeighborhoodIcon,
  [PredictionTypeEnum.PostalCode]: PinIcon,
  [PredictionTypeEnum.Address]: PinIcon,
};

interface ElasticAutocompleteProps {
  id?: string;
  locale: string;
  initialValue?: string | null;
  placeholder: string;
  countries?: string[] | null;
  showTabs?: boolean;
  evListingsOnly: boolean;
  disableCurrentLocation?: boolean;
  onPredictionClick: (prediction: Prediction, tabIndex: number) => void;
  onClear?: () => void;
}

/**
 * As of now we are pulling predictions from 2 different sources:
 * 1. S3 JSON file (for custom neighborhoods and addresses)
 * 2. Elastic Search API (for everything else)
 */
const ElasticAutocomplete: FC<ElasticAutocompleteProps> = observer(
  ({
    id = 'google-search-input',
    locale = 'en',
    placeholder,
    initialValue,
    countries = DEFAULT_COUNTRIES,
    showTabs = true,
    evListingsOnly,
    disableCurrentLocation = false,
    onPredictionClick,
    onClear,
  }) => {
    const { appStore, shopStore, searchStore } = useStores();
    const { geoInfo } = appStore;
    const { frequentlySearchedAreas, handleSaveSearchLog, recentSearches, saveRecentSearch } =
      searchStore;
    const { shopIds } = shopStore;
    const t = useTranslations();
    const posthog = usePostHog();
    const {
      isLoaded,
      geocodeCurrentLocation,
      pending: geolocationPending,
    } = useLocationToGeocode(config);
    const [searchValue, setSearchValue] = useState(initialValue ?? '');
    const [debouncedValue] = useDebounce(searchValue, 300);
    const [isFocused, setIsFocused] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);

    const {
      start: getPredictions,
      data: predictions,
      pending: predictionsLoading,
      reset: resetPredictions,
    } = useAction<PredictionsResponse>();

    const [expanded, setExpanded] = useState<Record<PredictionTypeEnum, boolean>>({
      [PredictionTypeEnum.Country]: false,
      [PredictionTypeEnum.State]: false,
      [PredictionTypeEnum.City]: false,
      [PredictionTypeEnum.CityRegion]: false,
      [PredictionTypeEnum.Neighborhood]: false,
      [PredictionTypeEnum.PostalCode]: false,
      [PredictionTypeEnum.Address]: false,
    });

    const predictionTypeTitles: Record<PredictionTypeEnum, string> = {
      [PredictionTypeEnum.Country]: t('PredictionType.countries'),
      [PredictionTypeEnum.State]: t('PredictionType.states'),
      [PredictionTypeEnum.City]: t('PredictionType.cities'),
      [PredictionTypeEnum.CityRegion]: t('PredictionType.regions'),
      [PredictionTypeEnum.Neighborhood]: t('PredictionType.neighborhoods'),
      [PredictionTypeEnum.PostalCode]: t('PredictionType.postalCodes'),
      [PredictionTypeEnum.Address]: t('PredictionType.listings'),
    };

    const predictionTypeTitlesSingular: Record<PredictionTypeEnum, string> = {
      [PredictionTypeEnum.Country]: t('PredictionType.country'),
      [PredictionTypeEnum.State]: t('PredictionType.state'),
      [PredictionTypeEnum.City]: t('PredictionType.city'),
      [PredictionTypeEnum.CityRegion]: t('PredictionType.region'),
      [PredictionTypeEnum.Neighborhood]: t('PredictionType.neighborhood'),
      [PredictionTypeEnum.PostalCode]: t('PredictionType.postalCode'),
      [PredictionTypeEnum.Address]: t('PredictionType.listing'),
    };

    useEffect(() => {
      if (!debouncedValue) {
        resetPredictions();
        return;
      }

      getPredictions({
        action: async () => {
          const propertyTypes = tabIndex === 0 ? propertyTypesToBuy : propertyTypesToRent;
          const res = await apiClient.getLocationPredictions({
            query: debouncedValue,
            locale: locale.slice(0, 2),
            evOnly: evListingsOnly,
            countries: countries ?? undefined,
            shopIds: shopIds ?? undefined,
            propertyTypes: propertyTypes ?? undefined,
            lat: geoInfo?.latitude,
            lng: geoInfo?.longitude,
            size: 10,
          });

          return { data: res.data };
        },
      });
    }, [debouncedValue, geoInfo, tabIndex]);

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
      if (!isFocused) {
        setIsFocused(true);
      }

      const amountRemoved = searchValue.length - e.target.value.length;
      if (amountRemoved > 1 && onClear) {
        onClear();
      }
    };

    const handlePredictionClick = async (prediction: Prediction) => {
      posthog.capture('search_prediction_click', { queryWhenClicked: searchValue, prediction });

      setSearchValue(prediction.text);
      setIsFocused(false);
      onPredictionClick(prediction, tabIndex);
      saveRecentSearch(prediction);
      handleSaveSearchLog(prediction);
    };

    const handleCurrentLocationClick = async () => {
      const results = await geocodeCurrentLocation();
      const firstResult = results[0];
      if (!firstResult) {
        return;
      }

      const city = firstResult.address_components.find((c) =>
        c.types.includes('locality'),
      )?.long_name;
      const state = firstResult.address_components.find((c) =>
        c.types.includes('administrative_area_level_1'),
      )?.short_name;
      const location = `${city}, ${state}`;

      setSearchValue(location);
      onPredictionClick(
        {
          text: location,
          type: PredictionTypeEnum.City,
          value: location,
          count: 0,
          addressComponents: {
            city,
            stateOrProvince: state,
          },
        },
        tabIndex,
      );
      setIsFocused(false);

      posthog.capture('search_use_current_location', { city, state });
    };

    const renderGroupHeader = (title: string, icon: any) => {
      return (
        <PredictionGroupHeader>
          <Image width={24} height={24} alt="" src={icon} />
          {title}
          <hr />
        </PredictionGroupHeader>
      );
    };

    const renderLocationGroup = (type: PredictionTypeEnum, locations: Prediction[]) => {
      const isExpanded = expanded[type];
      const itemCount = isExpanded ? 12 : 3;
      const displayedItems = locations.slice(0, itemCount);
      const toggleViewText = isExpanded
        ? t('View Less')
        : t('View More') + ` (${locations.length - itemCount})`;

      return (
        <PredictionGroup>
          {renderGroupHeader(predictionTypeTitles[type], predictionTypeIcons[type])}
          {displayedItems.map((location) => {
            return (
              <PredictionItem
                key={location.value}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePredictionClick(location);
                  }
                }}
                onClick={() => handlePredictionClick(location)}
              >
                <PredictionItemText>
                  {renderHighlightedText(location.text, searchValue)}
                </PredictionItemText>
              </PredictionItem>
            );
          })}

          {locations.length > 3 && (
            <ViewMoreButton onClick={() => setExpanded({ ...expanded, [type]: !isExpanded })}>
              {toggleViewText}
            </ViewMoreButton>
          )}
        </PredictionGroup>
      );
    };

    const renderHighlightedText = (text: string, query: string) => {
      if (!query) return text;
      const safeQuery = query.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'); // Escape special characters
      const regex = new RegExp(`(${safeQuery})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? <strong key={index}>{part}</strong> : part,
      );
    };

    const hasFrequentlySearchedAreas =
      frequentlySearchedAreas && Object.keys(frequentlySearchedAreas).length > 0;

    const resultsEmpty = Object.values(predictions || {}).every(
      (locations) => locations.length === 0,
    );

    return (
      <ClickAwayListener onClickAway={() => setIsFocused(false)}>
        <SearchWrapper>
          {showTabs && (
            <SearchTabs
              tabs={[
                { label: t('Buy'), testId: 'header-buy-btn' },
                { label: t('Rent'), testId: 'header-rent-btn' },
              ]}
              activeTab={tabIndex}
              onTabChange={(newIndex) => {
                setTabIndex(newIndex);
                setIsFocused(true);
              }}
            />
          )}

          <SearchInputWrapper>
            <SearchInput
              id={id}
              type="search"
              autoComplete="off"
              value={searchValue}
              placeholder={placeholder}
              onChange={handleSearchChange}
              onFocus={() => setIsFocused(true)}
              $isFocused={isFocused}
              $showTabs={showTabs}
            />

            <SearchInputIcon>
              {predictionsLoading || geolocationPending ? (
                <CircularProgress size={18} thickness={3} sx={{ color: '#666666' }} />
              ) : (
                <Magnifier size="18px" color="#666666" />
              )}
            </SearchInputIcon>
          </SearchInputWrapper>

          {isFocused && (
            <AutocompleteDropdown>
              {predictions ? (
                <>
                  {predictions.countries.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.Country, predictions.countries)}
                  {predictions.states.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.State, predictions.states)}
                  {predictions.cities.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.City, predictions.cities)}
                  {predictions.cityRegions.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.CityRegion, predictions.cityRegions)}
                  {predictions.neighborhoods.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.Neighborhood, predictions.neighborhoods)}
                  {predictions.postalCodes.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.PostalCode, predictions.postalCodes)}
                  {predictions.listings.length > 0 &&
                    renderLocationGroup(PredictionTypeEnum.Address, predictions.listings)}

                  {resultsEmpty && (
                    <div style={{ padding: '0 14px' }}>
                      {t('Unfortunately, no results match your search')}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {!disableCurrentLocation && isLoaded && (
                    <PredictionGroupHeader onClick={handleCurrentLocationClick} $isClickable>
                      <Image width={24} height={24} alt="" src={PinIcon} />
                      {t('Use Current Location')}
                    </PredictionGroupHeader>
                  )}

                  {recentSearches.length > 0 && (
                    <PredictionGroup>
                      {renderGroupHeader(t('Search History'), GlobeIcon)}
                      {recentSearches.map((prediction) => {
                        return (
                          <PredictionItem
                            key={prediction.value}
                            onClick={() => handlePredictionClick(prediction)}
                          >
                            <PredictionItemText>{prediction.text}</PredictionItemText>
                            <PredictionType>
                              {predictionTypeTitlesSingular[prediction.type]}
                            </PredictionType>
                          </PredictionItem>
                        );
                      })}
                    </PredictionGroup>
                  )}

                  {hasFrequentlySearchedAreas && (
                    <PredictionGroup>
                      {renderGroupHeader(t('Frequently Searched'), GlobeIcon)}
                      {Object.entries(frequentlySearchedAreas).map(([type, value]) => {
                        const prediction = JSON.parse(value) as Prediction;
                        return (
                          <PredictionItem
                            key={prediction.value}
                            onClick={() => handlePredictionClick(prediction)}
                          >
                            <PredictionItemText>{prediction.text} </PredictionItemText>
                            <PredictionType>{predictionTypeTitlesSingular[type]}</PredictionType>
                          </PredictionItem>
                        );
                      })}
                    </PredictionGroup>
                  )}
                </>
              )}
            </AutocompleteDropdown>
          )}
        </SearchWrapper>
      </ClickAwayListener>
    );
  },
);

export default ElasticAutocomplete;
